import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import stravaButtonImg from './btn_strava_connect_with_orange_x2.svg'; // Import Strava image

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://f155c6e67830.ngrok-free.app/v1';

const ConnectPage = () => {
  const [searchParams] = useSearchParams();
  const [userId, setUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const providerToAutoConnect = searchParams.get('provider');

  const handleConnect = useCallback(async (provider: string) => {
    if (!userId) {
      // This check is important for both manual and auto flows.
      setError('Não é possível conectar sem um ID de usuário.');
      return;
    }

    try {
      const params = new URLSearchParams({
        userId,
        ...({ chainHealthAuth: 'true' }),
      });
      let url = provider === 'garmin' ? `${API_BASE_URL}/auth/${provider}?${params}` : `${API_BASE_URL}/auth/${provider}?userId=${userId}`
      const response = await axios.get(url);
      const { authUrl } = response.data;

      if (authUrl) {
        window.location.href = authUrl;
      } else {
        setError(`Não foi possível obter la URL de autorização para ${provider}.`);
      }
    } catch (err: any) {
      console.error(`Error initiating ${provider} connection:`, err);
      setError(err.response?.data?.message || 'Ops! Houve um problema na comunicação com o provedor. Por favor, tente novamente em alguns instantes.');
    }
  }, [userId]);
  
  useEffect(() => {
    const id = searchParams.get('userId');
    if (id) {
      setUserId(id);
    } else {
      setError('ID de usuário não encontrado na URL. Por favor, use o link fornecido no WhatsApp.');
    }
  }, [searchParams]);

  // Effect for automatic connection
  useEffect(() => {
    // Trigger connect only if the userId is set and there's a provider in the URL
    if (providerToAutoConnect && userId) {
      handleConnect(providerToAutoConnect);
    }
  }, [userId, providerToAutoConnect, handleConnect]);


  if (error) {
    return (
      <div className="min-h-screen bg-transparent text-white flex flex-col items-center justify-center p-4">
        <h1 className="text-4xl font-bold mb-4 text-red-500">Ops! Algo deu errado</h1>
        <p className="text-lg text-center">{error}</p>
      </div>
    );
  }

  // While userId is loading, show a generic loading screen.
  if (!userId) {
    return (
      <div className="min-h-screen bg-transparent text-white flex flex-col items-center justify-center">
        <h1 className="text-4xl font-bold">Carregando...</h1>
      </div>
    );
  }

  // If an auto-connect is happening, show a specific redirecting message.
  if (providerToAutoConnect) {
    return (
      <div className="min-h-screen bg-transparent text-white flex flex-col items-center justify-center">
        <h1 className="text-4xl font-bold">Redirecionando para {providerToAutoConnect}...</h1>
        <p className="text-lg mt-4">Por favor, aguarde.</p>
      </div>
    );
  }

  // Default view with manual connection buttons
  return (
    <div className="min-h-screen bg-transparent text-white flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md mx-auto bg-black/50 backdrop-blur-lg p-8 rounded-xl border border-white/20">
        <h1 className="text-4xl font-bold mb-4 text-center ">Quase lá! Autorize o acesso aos seus dados</h1>
        <p className="text-lg mb-8 text-center">
          Para que nossa IA possa gerar insights sobre sua saúde e performance, precisamos que você autorize o acesso aos seus dados. É seguro e você mantém o controle.
        </p>
        
        <div className="flex flex-col items-center space-y-4">
          {/* Strava Button */}
          <button
            className="bg-transparent border-none p-0 cursor-pointer transition duration-300 px-6 ease-in-out transform hover:scale-105"
            onClick={() => handleConnect('strava')}
            aria-label="Conectar com Strava"
          >
            <img src={stravaButtonImg} alt="Botão para conectar com Strava"  className='w-[220px]'/>
          </button>

          {/* Oura Button 
          <button 
            className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg text-md transition duration-300 ease-in-out transform hover:scale-105"
            onClick={() => handleConnect('oura')}>
            Conectar com Oura
          </button>*/}
          <button 
            className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg text-md transition duration-300 ease-in-out transform hover:scale-105"
            onClick={() => handleConnect('garmin')}>
            Conectar com Garmin
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConnectPage;